/**
 * SMART MEMORY RESPONSE HANDLER - PRODUCTION VERSION
 * Fully integrated with ChatMode.jsx
 * Replaces static fallback with LLM-powered natural responses
 */

/**
 * Generate natural, context-aware response when memories aren't found
 * Uses LLM to create intelligent replies based on query analysis
 * 
 * @param {string} userQuery - What the user asked
 * @param {Array} searchResults - Memories that were found (empty array if none)
 * @param {Object} timeframeAnalysis - Analysis from detectConversationSummaryRequest
 * @param {Object} azureConfig - Azure OpenAI configuration
 * @param {string} userName - User's name (optional)
 * @returns {Promise<string>} - Natural LLM-generated response
 */
export const generateSmartMemoryResponse = async (
  userQuery,
  searchResults = [],
  timeframeAnalysis = {},
  azureConfig,
  userName = null
) => {
  if (!azureConfig?.endpoint || !azureConfig?.apiKey) {
    console.warn('⚠️ No Azure config - using fallback response');
    return generateFallbackMemoryResponse(userQuery, timeframeAnalysis, userName);
  }

  try {
    console.log('🧠 Generating smart memory response...');
    
    const hasResults = searchResults && searchResults.length > 0;
    const timeframe = timeframeAnalysis?.timeframe || 'unspecified';
    
    // Build context about what we found
    const searchContext = hasResults 
      ? `Found ${searchResults.length} memories matching this search:\n${searchResults
          .map(m => `- "${m.memory.substring(0, 60)}..."`)
          .join('\n')}`
      : 'No memories found for this search';

    // System prompt that guides LLM to respond naturally
    const systemPrompt = `You are Preptune, a helpful AI assistant. The user asked about a past conversation, but you didn't find any matching memories.

CONTEXT:
- User asked: "${userQuery}"
- Timeframe mentioned: ${timeframe}
- Search results: ${searchContext}

YOUR JOB: Generate a NATURAL, conversational response that:
1. Honestly tells them you don't have that memory
2. Explains WHY naturally (no data from that time, topic never discussed, etc.)
3. Invites them to share more in a warm, helpful way
4. Sounds like a real friend, NOT a bot

TONE: Warm, casual, conversational, helpful
LENGTH: 1-2 sentences max
VOICE: Natural human speech patterns

RULES:
✅ Be honest: "I don't have memories of that" or "We haven't chatted about that"
✅ Explain naturally: "because we just started talking" or "that topic hasn't come up yet"
✅ Invite them to share: "Tell me about it!", "What happened?", "Let's talk about it"
✅ Use their name occasionally if you have it
✅ Sound natural and genuine

❌ AVOID:
❌ "I don't remember that conversation. Can you give me more details?" (too robotic, generic)
❌ "I have no memories of..." (sounds like a database)
❌ "According to my records..." (corporate speak)
❌ Excessive apologizing
❌ Explaining how your memory works

EXAMPLES OF NATURAL RESPONSES:

User: "Remember when I told you about my job?"
Response: "I don't think we've talked about your work yet! What do you do?"

User: "What did we discuss yesterday?"
Response: "We didn't chat yesterday, so no memories from then. But what's new today?"

User: "Did I mention my exam?"
Response: "I don't have that in my memories. Is something coming up? Tell me about it!"

User: "Remember that thing we talked about?"
Response: "Hmm, that's too vague for me to search, but tell me again! What was it?"

User: "What was our conversation about?"
Response: "This is our first chat together! What's on your mind?"${userName ? `

USER INFO: The user's name is ${userName}, so you can use their name naturally (but don't force it in every sentence).` : ''}`;

    const response = await fetch(
      `${azureConfig.endpoint}/openai/deployments/${azureConfig.deployment}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': azureConfig.apiKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: systemPrompt
            },
            {
              role: 'user',
              content: `Generate a natural response for this memory search that found nothing.`
            }
          ],
          temperature: 0.75, // Slightly creative but not too random
          max_tokens: 100,
          top_p: 0.95
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ Azure API error:', response.status, errorData);
      return generateFallbackMemoryResponse(userQuery, timeframeAnalysis, userName);
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content?.trim();
    
    if (!content || content.length === 0) {
      console.warn('⚠️ Empty response from LLM');
      return generateFallbackMemoryResponse(userQuery, timeframeAnalysis, userName);
    }

    console.log('✅ Smart response generated:', content.substring(0, 60) + '...');
    return content;

  } catch (error) {
    console.error('❌ Smart response generation failed:', error);
    return generateFallbackMemoryResponse(userQuery, timeframeAnalysis, userName);
  }
};

/**
 * Intelligent fallback response when Azure isn't available or fails
 * Uses pattern matching to generate appropriate responses
 * 
 * @param {string} userQuery - What the user asked
 * @param {Object} timeframeAnalysis - Analysis from detectConversationSummaryRequest
 * @param {string} userName - User's name (optional)
 * @returns {string} - Natural fallback response
 */
const generateFallbackMemoryResponse = (userQuery, timeframeAnalysis = {}, userName = null) => {
  const queryLower = userQuery.toLowerCase().trim();
  const timeframe = timeframeAnalysis?.timeframe || 'unspecified';

  // Step 1: Check for timeframe-based responses
  if (timeframe === 'few minutes ago' || timeframe === 'few hours ago') {
    return "We just started talking! I don't have earlier memories from today yet. What would you like to chat about?";
  }

  if (timeframe === 'yesterday') {
    return "We didn't chat yesterday, so I don't have any memories from then. But let's catch up on what's happening now!";
  }

  if (timeframe === 'last week') {
    return "I don't have memories from that far back yet. Tell me what it was about!";
  }

  if (timeframe.includes('months ago') || timeframe.includes('year')) {
    return "That's before our conversations started. Tell me about it now!";
  }

  // Step 2: Analyze query type to determine response
  const isAskingAboutPerson = queryLower.match(/(?:tell me|who|about|person|friend|family|my|someone)/i);
  const isAskingAboutEvent = queryLower.match(/(?:when|event|happen|happened|date|time|day)/i);
  const isAskingAboutTopic = queryLower.match(/(?:discuss|talk|mention|said|topic|subject|conversation)/i);
  const isAskingAboutFeelings = queryLower.match(/(?:feel|emotion|mood|stress|happy|sad|anxious|excited)/i);
  const isAskingAboutWork = queryLower.match(/(?:work|job|career|project|meeting|deadline)/i);
  const isAskingAboutGoals = queryLower.match(/(?:goal|want|plan|dream|aim|achieve|hoping)/i);

  let response = '';

  // Step 3: Generate response based on query type
  if (isAskingAboutPerson) {
    response = `I don't think we've talked about that yet. Who are they? Tell me more!`;
    if (userName) {
      response = `Haven't heard about that person yet, ${userName}! Tell me about them!`;
    }
  } 
  else if (isAskingAboutWork) {
    response = `We haven't discussed your work yet! What do you do?`;
    if (userName) {
      response = `I don't have anything about your work yet, ${userName}. What's your job like?`;
    }
  }
  else if (isAskingAboutGoals) {
    response = `I don't have that in my memories. What are you working towards?`;
    if (userName) {
      response = `Tell me about your goals, ${userName}! What are you aiming for?`;
    }
  }
  else if (isAskingAboutEvent) {
    response = `I don't have memories of that event. What happened?`;
  }
  else if (isAskingAboutFeelings) {
    response = `I don't remember us discussing that. What's going on? How are you feeling?`;
    if (userName) {
      response = `How are you feeling, ${userName}? Tell me what's on your mind.`;
    }
  }
  else if (isAskingAboutTopic) {
    // Try to extract specific topic
    const topicMatch = queryLower.match(/about\s+([a-z\s]+)/i) || 
                       queryLower.match(/discuss(?:ed|ing)?\s+([a-z\s]+)/i) ||
                       queryLower.match(/mention(?:ed|ing)?\s+([a-z\s]+)/i);
    
    if (topicMatch) {
      const topic = topicMatch[1].trim();
      response = `We haven't talked about ${topic} yet. Tell me about it!`;
      if (userName) {
        response = `${topic.charAt(0).toUpperCase() + topic.slice(1)} hasn't come up between us, ${userName}. What about it?`;
      }
    } else {
      response = `I don't have memories of that conversation. Tell me again!`;
    }
  }
  else {
    // Generic fallback for unclear queries
    response = `I don't think we've chatted about that before. Tell me more!`;
    if (userName) {
      response = `Hmm, that's not ringing a bell, ${userName}! What were you thinking about?`;
    }
  }

  return response;
};

/**
 * INTEGRATION GUIDE - Add to your ChatMode.jsx
 * 
 * STEP 1: Import at the top of ChatMode.jsx
 * import { generateSmartMemoryResponse } from './path/to/smartMemoryResponses';
 * 
 * STEP 2: Find this section in handleSendMessage (around line 350-400):
 * 
 * OLD CODE TO REPLACE:
 * ===================================
 * if (recall?.found) {
 *   console.log('   ✅ Found:', recall.memories?.length || 0, 'memories');
 *   // ... handle found memories ...
 * } else {
 *   console.log('   📭 No memories found');
 *   
 *   const botMsgId = Date.now() + 2;
 *   const botMsg = {
 *     id: botMsgId,
 *     type: 'bot',
 *     content: "Hmm, I don't remember that conversation. Can you give me more details?",
 *     timestamp: new Date().toISOString()
 *   };
 *   
 *   setMessages(prev => [...prev, botMsg]);
 *   // ... rest of code ...
 * }
 * 
 * NEW CODE TO ADD:
 * ===================================
 * if (recall?.found) {
 *   console.log('   ✅ Found:', recall.memories?.length || 0, 'memories');
 *   // ... keep all existing memory found code ...
 * } else {
 *   console.log('   📭 No memories found - generating smart response');
 *   
 *   // ✅ NEW: Generate smart LLM response
 *   const smartResponse = await generateSmartMemoryResponse(
 *     userMessage,
 *     [], // No results
 *     summaryDetection, // Pass timeframe analysis
 *     {
 *       endpoint: AZURE_OPENAI_ENDPOINT,
 *       deployment: AZURE_OPENAI_DEPLOYMENT,
 *       apiKey: AZURE_OPENAI_API_KEY
 *     },
 *     userName // Pass username for personalization
 *   );
 *
 *   const botMsgId = Date.now() + 2;
 *   const botMsg = {
 *     id: botMsgId,
 *     type: 'bot',
 *     content: smartResponse, // ✅ DYNAMIC RESPONSE
 *     timestamp: new Date().toISOString()
 *   };
 *
 *   setMessages(prev => [...prev, botMsg]);
 *
 *   // ✅ Save to Firestore
 *   if (currentSessionId && firestoreService?.isReady?.()) {
 *     await firestoreService.saveConversation({
 *       id: currentSessionId,
 *       messages: [...messages, userMsg, botMsg],
 *       preview: messages[0]?.content || 'Chat',
 *       messageCount: [...messages, userMsg, botMsg].length,
 *       timestamp: new Date().toISOString(),
 *       lastUpdated: new Date().toISOString(),
 *       tag: 'memory-not-found'
 *     });
 *   }
 *
 *   scrollToBottom('smooth');
 *   return;
 * }
 */

/**
 * EXAMPLE RESPONSES FROM THIS IMPLEMENTATION
 * 
 * User: "Remember when I told you about my coding project?"
 * Smart: "I don't think we've talked about a coding project yet! What are you building?"
 * 
 * User: "What did we talk about yesterday?"
 * Smart: "We didn't chat yesterday, so I don't have any memories from then. But let's catch up on what's happening now!"
 * 
 * User: "Did I mention my exam?"
 * Smart: "I don't have that in my memories. Is something coming up? Tell me about it!"
 * 
 * User: "How did I feel about that meeting?"
 * Smart: "I don't remember us discussing that. What's going on? How are you feeling?"
 * 
 * User: "What were we talking about?"
 * Smart: "This is our first chat together! What's on your mind?"
 * 
 * User: "Remind me what we discussed last time"
 * Smart: "I don't have memories from that far back yet. Tell me what it was about!"
 */

/**
 * TEST THE IMPLEMENTATION
 * 
 * Add these test cases to verify it works:
 * 
 * Test 1: Recent memory not found
 * - User: "Remind me about my project"
 * - Expected: Natural response asking them to tell you about the project
 * 
 * Test 2: Yesterday's conversation
 * - User: "What did we talk about yesterday?"
 * - Expected: "We didn't chat yesterday, so I don't have any memories from then..."
 * 
 * Test 3: With user name
 * - Set userName to "Alex"
 * - User: "Do I have any goals saved?"
 * - Expected: Response with "Alex" naturally included
 * 
 * Test 4: Fallback when Azure is down
 * - Disconnect Azure or use invalid credentials
 * - User: "Remember when..."
 * - Expected: Fallback response (should still be natural, not generic)
 * 
 * Test 5: Vague query
 * - User: "Remember that?"
 * - Expected: "Hmm, that's too vague for me to search..."
 */

export default {
  generateSmartMemoryResponse,
  generateFallbackMemoryResponse
};